import {
  JourneyModel,
  JourneysStore,
  selectAllJourneys,
  selectSelectedJourney,
} from '@des/store/journeys';
import {
  select,
} from '@ngrx/store';
import {
  combineLatest,
  Observable,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
} from 'rxjs/operators';

/**
 * Gets the arrival endpoint for the last Journey in the order
 *
 * @param source The journey store
 * @returns Observable of the arrival flight end point for the last journey of the order
 */
const getLastJourneyArrival = (source: Observable<JourneysStore>) =>
  combineLatest([
    source.pipe(
      filter((store) => !!store.journeys),
      select(selectSelectedJourney),
      filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey?.journeyElements.length),
      map((selectedJourney) => selectedJourney.journeyElements[0].journeyElement.orderId),
      filter<string>((orderId) => !!orderId)
    ),
    source.pipe(
      filter((store) => !!store.journeys),
      select(selectAllJourneys),
      filter<JourneyModel[]>((journeys) => journeys.length > 0),
      map((journeys) => journeys.slice().reverse())
    )
  ]).pipe(
    map(([selectedJourneyOrderId, reversedJourneys]) => {
      const lastJourneyIndex = reversedJourneys.findIndex((journey) => journey.journeyElements[0].journeyElement.orderId === selectedJourneyOrderId);
      const lastJourney = reversedJourneys[lastJourneyIndex];
      return lastJourney.flights[lastJourney?.flights.length - 1].checkInFlight.arrival;
    }),
    distinctUntilChanged()
  );

/**
 * Arrival location code for the selected journey
 */
export const getArrivalLocationCode = () => (source: Observable<JourneysStore>) =>
  source.pipe(
    filter((store) => !!store.journeys),
    select(selectSelectedJourney),
    filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey),
    map((journey) => journey?.flights[journey?.flights.length - 1].checkInFlight.arrival.locationCode),
    distinctUntilChanged()
  );

/**
 * Arrival location city code for the selected journey
 */
export const getArrivalCityCode = () => (source: Observable<JourneysStore>) =>
  source.pipe(
    filter((store) => !!store.journeys),
    select(selectSelectedJourney),
    filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey),
    map((journey) => journey?.flights[journey?.flights.length - 1].checkInFlight.arrival.location?.cityCode),
    distinctUntilChanged()
  );

/**
 * Arrival location country code for the selected journey
 */
export const getArrivalCountryCode = () => (source: Observable<JourneysStore>) =>
  source.pipe(
    filter((store) => !!store.journeys),
    select(selectSelectedJourney),
    filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey),
    map((journey) => journey?.flights[journey?.flights.length - 1].checkInFlight.arrival.location?.countryCode),
    distinctUntilChanged()
  );

/**
 * Arrival location country code for the last journey
 */
export const getLastJourneyArrivalCountryCode = () => (source: Observable<JourneysStore>) =>
  getLastJourneyArrival(source).pipe(
    map((arrival) => arrival.location?.countryCode),
    distinctUntilChanged()
  );

/**
 * Arrival location city code for the last journey
 */
export const getLastJourneyArrivalCityCode = () => (source: Observable<JourneysStore>) =>
  getLastJourneyArrival(source).pipe(
    map((arrival) => arrival.location?.cityCode),
    distinctUntilChanged()
  );

/**
 * Arrival location code for the last journey
 */
export const getLastJourneyArrivalLocationCode = () => (source: Observable<JourneysStore>) =>
  getLastJourneyArrival(source).pipe(
    map((arrival) => arrival.locationCode),
    distinctUntilChanged()
  );
